// React
import * as React from 'react';

// Libraries
import * as AccordionPrimitive from '@radix-ui/react-accordion';

// Icons
import { ChevronDownIcon } from '@icons/ChevronDownIcon';

// Hooks and utilities
import { cn } from '@/lib/utils';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn('border-b border-grey-light', className)}
    {...props}
  />
));
AccordionItem.displayName = 'AccordionItem';

/** The trigger's chevron, exported so a custom trigger animates identically. */
const AccordionChevron = ({ className, ...props }: React.ComponentProps<'svg'>) => (
  <ChevronDownIcon
    aria-hidden="true"
    {...props}
    className={cn(
      'shrink-0 text-off-black accordion-chevron',
      'group-data-[state=open]/accordion:rotate-180',
      className,
    )}
  />
);
AccordionChevron.displayName = 'AccordionChevron';

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'group/accordion flex flex-1 items-center justify-between py-4 font-inter text-base font-semibold text-off-black text-left',
        className,
      )}
      {...props}
    >
      {children}
      <AccordionChevron />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down motion-reduce:animate-none"
    {...props}
  >
    <div className={cn(className)}>{children}</div>
  </AccordionPrimitive.Content>
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionChevron, AccordionContent };
